import { motion, AnimatePresence } from 'framer-motion';
import { AlertCircle, X } from 'lucide-react';

interface ConfirmActionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  variant?: 'danger' | 'info';
}

export function ConfirmActionModal({ 
  isOpen, 
  onClose, 
  onConfirm, 
  title, 
  message, 
  confirmText = 'Confirmar', 
  cancelText = 'Cancelar',
  variant = 'danger'
}: ConfirmActionModalProps) {
  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[1000] flex items-center justify-center p-6">
          {/* OVERLAY */}
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="absolute inset-0 bg-black/80 backdrop-blur-sm"
          />

          {/* MODAL CONTENT */}
          <motion.div 
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            className="relative w-full max-w-sm bg-slate-900/90 border border-white/10 backdrop-blur-2xl rounded-[32px] overflow-hidden shadow-[0_30px_60px_-12px_rgba(0,0,0,0.5)]"
          >
            <div className="p-8 flex flex-col items-center text-center">
              {/* ICON */}
              <div className={`w-16 h-16 rounded-3xl flex items-center justify-center mb-6 ${variant === 'danger' ? 'bg-red-500/20 text-red-500' : 'bg-primary-cyan/20 text-primary-cyan'}`}>
                <AlertCircle className="w-8 h-8" />
              </div>

              {/* TEXTS */}
              <h3 className="text-white font-black text-xl uppercase tracking-tight mb-2">{title}</h3>
              <p className="text-slate-400 text-sm font-medium leading-relaxed mb-8">{message}</p>

              {/* ACTIONS */}
              <div className="flex flex-col w-full gap-3">
                <button 
                  onClick={() => {
                    onConfirm();
                    onClose();
                  }}
                  className={`w-full py-4 rounded-2xl font-black text-sm uppercase tracking-widest transition-all active:scale-95 ${variant === 'danger' ? 'bg-red-500 text-white shadow-lg shadow-red-500/20' : 'bg-primary-cyan text-bg-dark shadow-lg shadow-primary-cyan/20'}`}
                >
                  {confirmText}
                </button>
                <button 
                  onClick={onClose}
                  className="w-full py-4 rounded-2xl font-black text-sm uppercase tracking-widest text-slate-500 hover:text-white transition-colors"
                >
                  {cancelText}
                </button>
              </div>
            </div>

            {/* DECORATIVE LINE */}
            <div className={`h-1.5 w-full ${variant === 'danger' ? 'bg-red-500' : 'bg-primary-cyan'}`} />
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
